import { computed, Injectable, signal } from '@angular/core'

import { UserRole } from '@yoshu-lib/constants/roles.enum'

import { User } from '@yoshu-lib/models/user.model'

/**
 * The `AuthStore` class is responsible for managing the authentication state of the application.
 * It provides reactive signals for the current user and login status, allowing components to
 * respond to changes in authentication state.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private user = signal<User | null>(null)
  readonly token = signal<string | null>(null)
  readonly role = signal<UserRole | null>(null)

  readonly currentUser = computed(() => this.user())

  setUser(user: User | null) {
    this.user.set(user)

    if (user) {
      this.role.set(user.role)
      this.token.set(user.access_token)
    } else {
      this.token.set(null)
    }
  }

  readonly isAdmin = computed(() => this.role() === UserRole.Admin)
  readonly isLoggedIn = computed(() => !!this.user())
  readonly isYoshuStaff = computed(
    () => this.user()?.email.toLowerCase().endsWith('@yoshu.ai') ?? false
  )

  private isLoggingIn = signal(false)

  setIsLoggingIn(value: boolean) {
    this.isLoggingIn.set(value)
  }

  getIsLoggingIn() {
    return this.isLoggingIn()
  }

  getUserLanguage() {
    return this.user()?.yoshu_language
  }
}
